import { pick as _pick } from 'lodash-es';
import { useState, useCallback, useMemo } from 'react';
import { Formik } from 'formik';
import { useMutation } from '@apollo/client/react';
import { makeStyles } from 'tss-react/mui';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Button from '@mui/material/Button';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsActive from '@mui/icons-material/NotificationsActive';
import NotificationsNone from '@mui/icons-material/NotificationsNone';
import { processMutationError } from '../../utils/formik';
import { updateGroupMembershipMutation } from '../../apollo/groups';
import GroupNotificationsForm, { formSchema } from '../groups/notifications-form';
import HostGroup from '../host-group';
import type { GroupMember } from '../../types/GroupMember';

const useStyles = makeStyles()((theme) => ({
  accordianSummary: {
    '& > .MuiAccordionSummary-content': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: theme.typography.body1.fontSize,
      minWidth: 0,
      [theme.breakpoints.down('sm')]: {
        display: 'block',
      },
    },
  },
  groupName: {
    minWidth: 0,
    flexShrink: 1,
    overflow: 'hidden',
    '& a': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  rowButtons: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    '& > *:not(:first-of-type)': {
      marginLeft: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
    },
  },
  summary: {
    marginRight: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },
  summaryIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: 20,
  },
}));

type Props = {
  groupMember: GroupMember;
};

const AccountGroupNotificationsListItem = (props: Props) => {
  const { groupMember } = props;
  const { classes } = useStyles();

  const { group } = groupMember;

  const [expanded, setExpanded] = useState(false);
  const handleAccordionChange = useCallback((e, isExpanded) => {
    setExpanded(isExpanded);
  }, []);

  const [updateGroupMembership] = useMutation(updateGroupMembershipMutation);

  const handleSubmit = useCallback((values, actions) => {
    updateGroupMembership({
      variables: {
        groupId: group.id,
        data: _pick(values, [
          'eventNotificationPreference',
          'postNotificationPreference',
        ]),
      },
    }).then(() => {
      actions.setSubmitting(false);
      // todo: show that it's saved
    }).catch((error) => {
      processMutationError(error, actions);
    });
  }, [group, updateGroupMembership]);

  const subscribedItems = useMemo(() => {
    const { eventNotificationPreference, postNotificationPreference } = groupMember;

    const items = [];

    if (eventNotificationPreference === 'ALL') {
      items.push('Events');
    }

    if (postNotificationPreference === 'FOLLOWING') {
      items.push('Posts');
    } else if (postNotificationPreference === 'ALL') {
      items.push('Posts and comments');
    }

    return items;
  }, [groupMember]);

  const handleGroupNameClick = useCallback((e) => e.stopPropagation(), []);

  // minimal effort to not render found items
  if (group.found) {
    return null;
  }

  return (
    <Formik
      initialValues={groupMember}
      validationSchema={formSchema}
      onSubmit={handleSubmit}
    >
      <Accordion
        expanded={expanded}
        onChange={handleAccordionChange}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-label="Expand"
          aria-controls={`group-member-${groupMember.id}-content`}
          id={`group-member-${groupMember.id}-header`}
          className={classes.accordianSummary}
        >
          <HostGroup
            group={group}
            simple
            onClick={handleGroupNameClick}
            className={classes.groupName}
          />
          <div className={classes.rowButtons}>
            <div className={classes.summary}>
              {subscribedItems.length > 0 ?
                <NotificationsActive className={classes.summaryIcon} /> :
                <NotificationsNone className={classes.summaryIcon} />}
              {subscribedItems.length > 0 ? subscribedItems.join(', ') : 'No notifications'}
            </div>
            <Button
              variant="outlined"
              size="small"
              component="span"
              tabIndex={-1}
            >
              Edit Notifications
            </Button>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <GroupNotificationsForm
            name={`create-group-member-notifications-${groupMember.id}`}
          />
        </AccordionDetails>
      </Accordion>
    </Formik>
  );
}

export default AccountGroupNotificationsListItem;
